import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ApplicationInsights, ITelemetryItem, SeverityLevel } from '@microsoft/applicationinsights-web';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: "root",
})
export class AppInsightsService {
  readonly appInsights?: ApplicationInsights;
  private version?: string;
  private roleName?: string;
  private roleInstance?: string;
  private filters: ((item: ITelemetryItem) => boolean)[] = [];

  constructor(private router: Router) {
    if (environment.aiConnectionString) {
      this.addTelemetryFilter((item) => {
        if (Array.isArray(item.baseData?.exceptions)) {
          if (item.baseData.exceptions.some(e => e.message?.includes('ResizeObserver loop completed with undelivered notifications.'))) {
            return false;
          }
        }
        return true;
      });

      const angularPlugin = new AngularPlugin();
      const appInsights = new ApplicationInsights({
        config: {
          connectionString: environment.aiConnectionString,
          enableCorsCorrelation: true,
          enableRequestHeaderTracking: true,
          enableResponseHeaderTracking: true,
          extensions: [angularPlugin],
          extensionConfig: {
            [angularPlugin.identifier]: { router: this.router }
          }
        }
      });
      appInsights.addTelemetryInitializer(item => this.telemetryInitializer(item));
      appInsights.addDependencyInitializer(item => {
        if (item.item.name) {
          // remove /xpac/<id> from the dependency, which are named like "GET /xpac/84F64C000001/api/v1/devices""
          item.item.name = item.item.name.replace(/^([A-Z]+)\s+\/xpac\/[0-9a-fA-F]+/, "$1 ");
          // remove /xpac/<id> from names which do not have a HTTP method prefix
          item.item.name = item.item.name.replace(/^\/xpac\/[0-9a-fA-F]+/, '/');
        }
        return true;
      });
      appInsights.loadAppInsights();
      this.appInsights = appInsights;

      console.log('Initialized Application Insights');

      // For builds we want to get all our console logging in Application Insights, but for development PCs this isn't necessary
      if (environment.production) {
        this.patchConsole();
      }
    }
  }

  public setVersion(version: string): void {
    this.version = version;
  }

  public setRoleName(roleName: string): void {
    this.roleName = roleName;
  }

  public setRoleInstance(roleInstance: string): void {
    this.roleInstance = roleInstance;
  }

  public setUser(email?: string): void {
    this.appInsights?.setAuthenticatedUserContext(email, null, true);
  }

  public logPageView(name?: string, url?: string): void {
    this.appInsights?.trackPageView({
      name: name,
      uri: url
    });
  }

  public logEvent(name: string, properties?: { [key: string]: any }): void {
    this.appInsights?.trackEvent({ name: name}, properties);
  }

  public logMetric(name: string, average: number, properties?: { [key: string]: any }): void {
    this.appInsights?.trackMetric({ name: name, average: average }, properties);
  }

  public logException(exception: Error, severityLevel?: number | SeverityLevel): void {
    this.appInsights?.trackException({ exception: exception, severityLevel: severityLevel });
  }

  public logTrace(message: string, properties?: { [key: string]: any }, severityLevel?: number | SeverityLevel): void {
    this.appInsights?.trackTrace({ message: message, severityLevel: severityLevel }, properties);
  }

  public addTelemetryFilter(filter: (item: ITelemetryItem) => boolean): void {
    this.filters.push(filter);
  }

  private telemetryInitializer(item: ITelemetryItem): boolean {
    if (this.filters.some(f => !f(item))) {
      return false;
    }

    if (this.version) {
      item.tags['ai.application.ver'] = this.version;
    }
    if (this.roleName) {
      item.tags['ai.cloud.role'] = this.roleName;
    }
    if (this.roleInstance) {
      item.tags['ai.cloud.roleInstance'] = this.roleInstance;
    }

    return true;
  }

  private patchConsole(): void {
    const self = this;
    function patch<T extends Function>(method: T, severityLevel: SeverityLevel): any {
      const getCircularReplacer = () => {
        const seen = new WeakSet();
        return (key: any, value: any) => {
          if (typeof value === "object" && value !== null) {
            if (seen.has(value)) {
              return;
            }
            seen.add(value);
          }
          return value;
        };
      };

      return function() {
        method.apply(console, arguments);
        let text = '';
        let error: Error = null;
        for (const arg of arguments) {
          try {
            if (text.length > 0) {
              text += ', ';
            }
            if (typeof arg === 'string') {
              text += arg;
            }
            else if (typeof arg === 'object') {
              if (arg instanceof Error) {
                error = arg;
                text += `${arg.name}: ${arg.message}`;
              } else {
                text += JSON.stringify(arg, getCircularReplacer());
              }
            }
            else {
              text += arg;
            }
          }
          catch (error: any) {
            text += `{Logging Error: ${error}}`;
          }
        }
        self.logTrace(text, undefined, severityLevel);
      }
    }

    console.error = patch(console.error, SeverityLevel.Error);
    console.warn = patch(console.warn, SeverityLevel.Warning);

    // console.log = patch(console.log, SeverityLevel.Information);
    // console.debug = patch(console.debug, SeverityLevel.Verbose);
    // console.trace = patch(console.trace, SeverityLevel.Verbose);
  }
}
